import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Target, FileText, Upload, Play, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ExtractionForm({ onStartExtraction }) {
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    topics: '',
    userPrompt: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file count
    if (selectedFiles.length > 10) {
      toast.error('Maximum 10 PDF files allowed');
      return;
    }
    
    // Validate file types
    const invalidFiles = selectedFiles.filter(file => file.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setFiles(selectedFiles);
    toast.success(`${selectedFiles.length} file(s) selected`);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for topics (required)
    if (!formData.topics.trim()) {
      toast.error('Please enter at least one topic');
      return;
    }

    // Upload mode validation
    if (files.length === 0) {
      toast.error('Please select at least one PDF file');
      return;
    }
    
    // Prepare multipart form data
    const uploadFormData = new FormData();
    files.forEach(file => {
      uploadFormData.append('files', file);
    });
    uploadFormData.append('topics', formData.topics);
    if (formData.userPrompt.trim()) {
      uploadFormData.append('user_prompt', formData.userPrompt);
    }
    
    onStartExtraction({
      mode: 'upload',
      formData: uploadFormData,
      fileCount: files.length
    });
    
    toast.success('Extraction started successfully!');
  };

  return (
    <Card className="shadow-lg border-border/50">
      <CardHeader className="space-y-1 pb-6">
        <CardTitle className="text-2xl font-bold">Configure Extraction</CardTitle>
        <CardDescription className="text-base">
          Upload PDF files for AI-powered data extraction
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="fileUpload" className="text-sm font-medium flex items-center gap-2">
              <Upload className="h-4 w-4 text-primary" />
              Upload PDF Files (Max 10)
            </Label>
            <div className="space-y-3">
              <Input
                id="fileUpload"
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileChange}
                className="h-11 transition-smooth cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Select up to 10 PDF files. Each PDF should have maximum 20 pages.
              </p>
              
              {/* Selected Files List */}
              {files.length > 0 && (
                <div className="space-y-2 mt-3">
                  <p className="text-sm font-medium text-foreground">{files.length} file(s) selected:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-border rounded-lg p-3">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm text-foreground">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Topics (Required) */}
          <div className="space-y-2">
            <Label htmlFor="topics" className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-accent" />
              Topics to Extract *
            </Label>
            <Input
              id="topics"
              type="text"
              placeholder="Name, Address, Date"
              value={formData.topics}
              onChange={(e) => handleInputChange('topics', e.target.value)}
              className="h-11 transition-smooth"
            />
            <p className="text-xs text-muted-foreground">
              Enter comma-separated topics or keywords to extract from the PDFs
            </p>
          </div>

          {/* User Prompt (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="userPrompt" className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-secondary" />
              Custom Extraction Prompt (Optional)
            </Label>
            <Textarea
              id="userPrompt"
              placeholder="Add optional instructions to refine extraction — for example, if multiple names appear in the document, extract all of them as a comma‑separated list."
              value={formData.userPrompt}
              onChange={(e) => handleInputChange('userPrompt', e.target.value)}
              className="min-h-[100px] resize-none transition-smooth"
            />
            <p className="text-xs text-muted-foreground">
              Provide specific instructions or context to guide the extraction
            </p>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold gradient-primary hover:opacity-90 transition-smooth shadow-accent"
            >
              <Play className="mr-2 h-5 w-5" />
              Start Extraction
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
